import { createContext, useContext, useMemo, useState } from 'react';

interface CanvasFreeElementContextType {
  setCreateElementActive: (element: 'section' | 'text-node' | 'sticky-note' | null) => void;
  createSectionActive: boolean;
  createTextNodeActive: boolean;
  createStickyNoteActive: boolean;
  currentCreateElementActive: 'section' | 'text-node' | 'sticky-note' | null;
  _isCtxInitialized: boolean;
  isCreatingTemporaryElement: boolean;
  setIsCreatingTemporaryElement: (isCreatingTemporaryElement: boolean) => void;
}

const CanvasFreeElementContext = createContext<CanvasFreeElementContextType>({
  _isCtxInitialized: false,
  setCreateElementActive: () => {},
  createSectionActive: false,
  createTextNodeActive: false,
  createStickyNoteActive: false,
  currentCreateElementActive: null,
  isCreatingTemporaryElement: false,
  setIsCreatingTemporaryElement: () => {},
});

export const CanvasFreeElementContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [currentCreateElement, setCurrentCreateElement] = useState<
    'section' | 'text-node' | 'sticky-note' | null
  >(null);
  const [isCreatingTemporaryElement, setIsCreatingTemporaryElement] = useState(false);

  const createSectionActive = currentCreateElement === 'section';
  const createTextNodeActive = currentCreateElement === 'text-node';
  const createStickyNoteActive = currentCreateElement === 'sticky-note';

  return (
    <CanvasFreeElementContext.Provider
      value={useMemo(
        () => ({
          createSectionActive,
          createTextNodeActive,
          createStickyNoteActive,
          currentCreateElementActive: currentCreateElement,
          setCreateElementActive: setCurrentCreateElement,
          isCreatingTemporaryElement,
          setIsCreatingTemporaryElement,
          _isCtxInitialized: true,
        }),
        [
          createSectionActive,
          createStickyNoteActive,
          createTextNodeActive,
          currentCreateElement,
          isCreatingTemporaryElement,
        ],
      )}
    >
      {children}
    </CanvasFreeElementContext.Provider>
  );
};

export const useCanvasFreeElementContext = (): CanvasFreeElementContextType & {
  isCreatingFreeElement: boolean;
} => {
  const ctx = useContext(CanvasFreeElementContext);
  if (!ctx._isCtxInitialized) {
    throw new Error('CanvasFreeElementContext not initialized');
  }
  const isCreatingFreeElement = useMemo(() => {
    return ctx.currentCreateElementActive !== null || ctx.isCreatingTemporaryElement;
  }, [ctx.currentCreateElementActive, ctx.isCreatingTemporaryElement]);

  return useMemo(() => ({ ...ctx, isCreatingFreeElement }), [ctx, isCreatingFreeElement]);
};
